import React from 'react';

import { getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import useError from '@smart-tv/hooks/use-error';

export interface DialogContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface DialogContextValues {
  opened: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  closedDialogFocusKey: React.MutableRefObject<string>;
  handleSetDialogFocusKey: (focusKey: string) => void;
}

export const DialogContext = React.createContext({} as DialogContextValues);

export const DialogProvider = (props: DialogContextProps) => {
  const { children } = props;
  const { isPlayingRef, player, playerRootElem } = usePlayerContext();
  const { errorOpen } = useError();

  const playerRef = React.useRef(player);
  const errorOpenRef = React.useRef(errorOpen);
  const playerRootElemRef = React.useRef(playerRootElem);
  playerRef.current = player;
  errorOpenRef.current = errorOpen;
  playerRootElemRef.current = playerRootElem;

  const [opened, setOpened] = React.useState(false);
  const closedDialogFocusKey = React.useRef('');
  const layersOpen = React.useRef<number>(0);

  const handleLayerOpen = React.useCallback(() => {
    layersOpen.current += 1;
  }, []);

  const handleLayerClose = React.useCallback(() => {
    layersOpen.current = Math.max(layersOpen.current - 1, 0);
  }, []);

  const handleOpen = React.useCallback(() => {
    closedDialogFocusKey.current = getCurrentFocusKey();
    if (document.body.style.overflow !== 'hidden') {
      document.body.style.setProperty('overflow', 'hidden');
    } else {
      handleLayerOpen();
    }
    setOpened(true);
  }, [handleLayerOpen]);

  const handleClose = React.useCallback(() => {
    if (closedDialogFocusKey.current && !playerRef.current && !errorOpenRef.current) {
      setFocus(closedDialogFocusKey.current);
    }
    if (playerRef.current && playerRootElemRef.current) {
      playerRootElemRef.current.focus();
    }
    if (!isPlayingRef.current && layersOpen.current === 0) {
      document.body.style.removeProperty('overflow');
    } else {
      handleLayerClose();
    }

    setOpened(false);
  }, [handleLayerClose, isPlayingRef]);

  const handleSetDialogFocusKey = React.useCallback((focusKey: string) => {
    closedDialogFocusKey.current = focusKey;
  }, []);

  return (
    <DialogContext.Provider
      value={{
        opened,
        handleOpen,
        handleClose,
        closedDialogFocusKey,
        handleSetDialogFocusKey
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export default DialogContext;
